import { Component, computed, effect, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { IManualDeliverableStatus } from '@interfaces/campaign-analytics-api.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { DeliverableUtilitiesService } from '@utilities/deliverable-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { IDeliverableCard } from '../cta-analytics-details-deliverable-card.interface';
import { ManualDeliverableStatusMapStore } from './manual-deliverable-status-map.store';
import { ManualDeliverableStatusStore } from './manual-deliverable-status.store';
import { EManualDeliverableStatusAction } from './manual-devlierable-status.interface';

@Component({
  imports: [
    MatMenuModule,
    TranslateModule,
    MatTooltipModule,
    NgxSkeletonLoaderModule,
    MatButtonModule,
    DotsSpinnerComponent,
    IconComponent,
  ],
  providers: [ManualDeliverableStatusStore],
  selector: 'app-manual-deliverable-status',
  templateUrl: './manual-deliverable-status.component.html',
})
export class ManualDeliverableStatusComponent {
  public readonly manualDeliverableStatus = input.required<IManualDeliverableStatus | TNil>();
  public readonly deliverableCard = input.required<IDeliverableCard>();
  public readonly actionsVisible = input.required<boolean>();
  public readonly statusVisible = input.required<boolean>();
  public readonly creatorBriefingMode = input.required<boolean>();
  public readonly exportMode = input.required<boolean>();

  public readonly hideTooltip = input<boolean>(false);

  public readonly updatedStatusManual = output<EAnalyticsStatus | TNil>();

  public readonly EManualDeliverableStatusAction = EManualDeliverableStatusAction;

  public readonly manualMenu = computed(() => {
    const status = this.getManualStatus();

    if (status === EAnalyticsStatus.ok) {
      return {
        alt: 'Accepted',
        menu: [
          { key: EManualDeliverableStatusAction.reject, label: 'shared.deliverableActions.manuallyReject' },
          { key: EManualDeliverableStatusAction.remove, label: 'shared.deliverableActions.removeManualStatus' },
        ],
      };
    }

    if (status === EAnalyticsStatus.failed) {
      return {
        alt: 'Rejected',
        menu: [
          { key: EManualDeliverableStatusAction.accept, label: 'shared.deliverableActions.manuallyAccept' },
          { key: EManualDeliverableStatusAction.remove, label: 'shared.deliverableActions.removeManualStatus' },
        ],
      };
    }

    return {
      alt: 'Dots',
      menu: [
        { key: EManualDeliverableStatusAction.accept, label: 'shared.deliverableActions.manuallyAccept' },
        { key: EManualDeliverableStatusAction.reject, label: 'shared.deliverableActions.manuallyReject' },
      ],
    };
  });

  public readonly isTrackingDeliverable = computed(() => {
    return this.deliverableUtilitiesService.isTrackingDeliverable(this.deliverableCard().type);
  });

  public readonly colors = computed(() => {
    const status = this.status();

    switch (status) {
      case EAnalyticsStatus.ok:
        if (this.isTrackingDeliverable()) {
          return {
            background: 'var(--shikenso-info-light)',
            text: 'var(--shikenso-info-base)',
          };
        }

        return {
          background: 'var(--shikenso-success-light)',
          text: 'var(--shikenso-success-base)',
        };
      case EAnalyticsStatus.failed:
        if (this.isTrackingDeliverable()) {
          return {
            background: 'var(--shikenso-info-light)',
            text: 'var(--shikenso-info-base)',
          };
        }

        return {
          background: 'var(--shikenso-error-light)',
          text: 'var(--shikenso-error-base)',
        };
      case EAnalyticsStatus.problem:
        return {
          background: 'var(--shikenso-warning-light)',
          text: 'var(--shikenso-warning-base)',
        };
      default:
        return {
          background: 'var(--shikenso-info-light)',
          text: 'var(--shikenso-info-base)',
        };
    }
  });

  public readonly updatedManualDeliverableStatus = computed(() => {
    return this.store.manualDeliverableStatus$() ?? this.manualDeliverableStatus();
  });

  public readonly updatedStatus = computed(() => {
    this.statusMapStore.updateVersion$();

    return this.statusMapStore.getStatus(`${this.deliverableCard().creatorId}_${this.deliverableCard().id}`);
  });

  public readonly status = computed(() => {
    if (this.updatedStatus() !== undefined) {
      if (this.updatedStatus() === null) {
        return this.deliverableCard().statusManual ?? this.deliverableCard().status;
      }

      return this.updatedStatus();
    }

    return (
      this.manualDeliverableStatus()?.status ?? this.deliverableCard().statusManual ?? this.deliverableCard().status
    );
  });

  public readonly statusLabel = computed(() => {
    const status = this.status();

    if (!status) {
      return this.translate.instant('shared.analyticsStatus.processing');
    }

    if (this.getManualStatus()) {
      if (status === EAnalyticsStatus.ok) {
        return this.translate.instant('shared.analyticsStatus.manuallyAccepted');
      }

      if (status === EAnalyticsStatus.failed) {
        return this.translate.instant('shared.analyticsStatus.manuallyRejected');
      }
    }

    if (this.isTrackingDeliverable()) {
      if (status === EAnalyticsStatus.ok) {
        return this.translate.instant('shared.statuses.findings');
      }

      if (status === EAnalyticsStatus.failed) {
        return this.translate.instant('shared.statuses.noFindings');
      }
    }

    return this.translate.instant(`shared.analyticsStatus.${this.status()?.toLowerCase()}`);
  });

  public readonly hasManualStatus = computed(() => {
    return !!this.getManualStatus();
  });

  public readonly tooltipHidden = computed(() => {
    return this.hideTooltip() || this.creatorBriefingMode() || this.exportMode();
  });

  public readonly store = inject(ManualDeliverableStatusStore);
  private readonly statusMapStore = inject(ManualDeliverableStatusMapStore);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly deliverableUtilitiesService = inject(DeliverableUtilitiesService);
  private readonly eventTrackingService = inject(EventTrackingService);

  constructor() {
    effect(() => {
      const loading = this.store.loading$();
      const manualDeliverableStatus = this.store.manualDeliverableStatus$();
      const error = this.store.error$();

      if (!loading && manualDeliverableStatus && !error) {
        const { status } = manualDeliverableStatus;
        const key = `${this.deliverableCard().creatorId}_${this.deliverableCard().id}`;

        this.statusMapStore.updateStatus({ key, status });
        this.updatedStatusManual.emit(status);
        this.snackbarService.openSuccess(this.translate.instant('shared.deliverableActions.success'));

        this.eventTrackingService.track(EEventType.campaignAnalyticsManualDeliverableStatusSet, {
          campaignId: this.deliverableCard().campaignId,
          contentStatus: this.deliverableCard().status,
          deliverableId: this.deliverableCard().id,
          deliverableType: this.deliverableCard().type,
          socialContentId: this.deliverableCard().socialContentId,
          systemStatus: status ?? undefined,
        });
      }
    });
  }

  public onManualMenuClick(
    action: EManualDeliverableStatusAction,
    event: MouseEvent,
    menuTrigger: MatMenuTrigger,
  ): void {
    event.stopPropagation();
    menuTrigger.closeMenu();

    const id = this.updatedManualDeliverableStatus()?.id;
    const newStatus = action ? (action.toString() as EAnalyticsStatus) : null;

    const payload: IManualDeliverableStatus = {
      ...(id ? { id } : {}),
      campaignId: this.deliverableCard().campaignId,
      creatorId: this.deliverableCard().creatorId,
      ctaTemplateId: this.deliverableCard().ctaTemplateId,
      deliverableId: this.deliverableCard().id,
      processed: false,
      socialContentId: this.deliverableCard().socialContentId,
      status: newStatus,
    };

    if (id) {
      this.store.updateManualDeliverableStatus(payload);
    } else {
      this.store.createManualDeliverableStatus(payload);
    }
  }

  private getManualStatus(): EAnalyticsStatus | TNil {
    if (this.store.manualDeliverableStatus$()) {
      return this.store.manualDeliverableStatus$()?.status;
    }

    // Map store reflects fresh API data or in-session user changes
    const mapStatus = this.updatedStatus();

    if (mapStatus !== undefined) {
      return mapStatus;
    }

    if (this.manualDeliverableStatus()?.processed) {
      return this.deliverableCard().statusManual;
    }

    return this.manualDeliverableStatus()?.status ?? this.deliverableCard().statusManual;
  }
}
